import { ChainData, ChainType, Token } from "@0xsquid/squid-types";
import { BASE_FEE } from "@stellar/stellar-sdk";
import { useQuery } from "@tanstack/react-query";
import { Interface } from "ethers";
import { erc20Abi } from "viem";
import { getClient } from "../../../core/client";
import { getFeeRate } from "../../../core/connectors/bitcoin/helpers";
import { CHAIN_IDS, nativeEvmTokenAddress } from "../../../core/constants";
import { keys } from "../../../core/queries/queries-keys";
import { scaleWeiToHbar } from "../../../services/internal/hederaService";
import { convertTempoFeeToStablecoinUnits } from "../../../services/internal/tempoService";

export function useSendTransactionGas({
  chain,
  token,
  from,
}: {
  chain: ChainData | undefined;
  token: Token | undefined;
  from: string | undefined;
}) {
  return useQuery<bigint>({
    queryKey: keys().sendTransactionGas(chain?.chainId, token?.address, from),
    queryFn: async () => {
      if (!chain || !token) throw new Error("Missing params");

      switch (chain.chainType) {
        case ChainType.EVM: {
          const client = await getClient<ChainType.EVM>(chain);
          const feeData = await client.getFeeData();

          if (!feeData.maxFeePerGas) return BigInt(0);

          let maxFeePerGas = feeData.maxFeePerGas;

          if (token.chainId === CHAIN_IDS.HEDERA) {
            maxFeePerGas = scaleWeiToHbar(BigInt(feeData.maxFeePerGas));
          }

          const dummyAddress = "0x1111111111111111111111111111111111111111";
          // Some RPC providers require the sender to have enough balance, otherwise estimation reverts
          // so we'll try to use the user provided address when possible
          const sender = from || dummyAddress;

          // Tempo has no native token, so `value` transfers are rejected by the EVM.
          // All transfers are ERC20 transfers
          const supportsNativeTransfers = chain.chainId !== CHAIN_IDS.TEMPO;
          const isNativeToken =
            token.address.toLowerCase() === nativeEvmTokenAddress.toLowerCase();

          if (isNativeToken && supportsNativeTransfers) {
            const gas = await client.estimateGas({
              from: sender,
              to: dummyAddress,
              value: 1,
              chainId: chain.chainId,
            });

            return gas * maxFeePerGas;
          }

          const erc20Interface = new Interface(erc20Abi);

          const data = erc20Interface.encodeFunctionData("transfer", [
            dummyAddress,
            1,
          ]);

          const gas = await client.estimateGas({
            from: sender,
            to: token.address,
            data,
            chainId: chain.chainId,
          });

          const feeWei = gas * maxFeePerGas;

          // Tempo gas fees are denominated 18 units,
          // this value needs to be scaled to 6 decimals
          if (chain.chainId === CHAIN_IDS.TEMPO) {
            return convertTempoFeeToStablecoinUnits(feeWei);
          }

          return feeWei;
        }
        case ChainType.COSMOS: {
          // TODO: get gas estimation from backend
          return BigInt(0);
        }
        case ChainType.SOLANA: {
          // TODO: get gas estimation from backend
          return BigInt(5000);
        }
        case ChainType.BTC: {
          const feeRate = await getFeeRate();

          // average vBytes for a segwit tx (2 inputs, 2 outputs)
          const vBytes = 378;
          const feeSats = feeRate * vBytes;

          // TODO: get gas estimation from backend?
          return BigInt(feeSats);
        }
        case ChainType.SUI: {
          // TODO: get gas estimation from backend
          return BigInt(0);
        }
        case ChainType.XRPL: {
          // TODO: get gas estimation from backend
          return BigInt(0);
        }
        case ChainType.STELLAR: {
          // TODO: get gas estimation from backend
          return BigInt(BASE_FEE) * BigInt(2);
        }

        default:
          throw new Error(
            `Gas estimation not implemented for chain type ${chain.chainType}`
          );
      }
    },
    enabled: !!chain && !!token,
    // 5 minutes
    staleTime: 1_000 * 60 * 5,
    refetchInterval: 1_000 * 60 * 5,
  });
}
